import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Plus } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type {
  AdminColumn,
  BulkAction,
  Fetcher,
  RowAction,
  Selection,
} from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchManualTxns,
  fetchRefunds,
  fetchSubscriptions,
  fetchWithdrawals,
  FIN_STR as S,
  type FinanceRow,
} from './financeApi'

/** Which finance workflow list this table reads (fetcher built internally so its
 *  identity stays stable across renders). */
export type QueueSource =
  | { source: 'refunds'; kind: string; status: string }
  | { source: 'withdrawals'; status: string }
  | { source: 'subscriptions'; status: string }
  | { source: 'manual' }

/**
 * A10 workflow jadvali (refund/dispute navbati, yechish so'rovlari, obunalar,
 * qo'lda kompensatsiya). Ro'yxat + qo'shish-forma + qatorli amallar (tasdiqlash/
 * rad/bekor) + ixtiyoriy bulk. Har amal toast + reload bilan o'raladi.
 */
export interface QueueField {
  name: string
  placeholder: string
  type?: 'text' | 'number' | 'select'
  mono?: boolean
  options?: { value: string; label: string }[]
}

export interface QueueAction<T> {
  id: string
  label: string
  icon?: ReactNode
  danger?: boolean
  visible?: (r: T) => boolean
  run: (r: T) => Promise<unknown>
}

export interface QueueBulk<T> {
  id: string
  label: string
  icon?: ReactNode
  danger?: boolean
  run: (rows: T[]) => Promise<unknown>
}

export interface FinanceQueueTableProps<T extends FinanceRow> {
  screenId: string
  title: string
  description?: string
  icon: ReactNode
  data: QueueSource
  searchKeys: string[]
  columns: AdminColumn<T>[]
  addLabel?: string
  fields?: QueueField[]
  /** Resolve to a string to override the success toast (e.g. new balance). */
  onCreate?: (form: Record<string, string>) => Promise<unknown>
  actions?: QueueAction<T>[]
  bulkActions?: QueueBulk<T>[]
  selectable?: boolean
}

function buildQueueFetcher<T extends FinanceRow>(data: QueueSource, keys: string[]): Fetcher<T> {
  switch (data.source) {
    case 'refunds':
      return fetchRefunds<T>(data.kind, data.status, keys)
    case 'withdrawals':
      return fetchWithdrawals<T>(data.status, keys)
    case 'subscriptions':
      return fetchSubscriptions<T>(data.status, keys)
    case 'manual':
      return fetchManualTxns<T>(keys)
  }
}

export function FinanceQueueTable<T extends FinanceRow>({
  screenId,
  title,
  description,
  icon,
  data,
  searchKeys,
  columns,
  addLabel,
  fields,
  onCreate,
  actions,
  bulkActions,
  selectable,
}: FinanceQueueTableProps<T>) {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [form, setForm] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const reload = () => setReloadKey((k) => k + 1)

  const dataKey = JSON.stringify(data)
  const searchKeyStr = searchKeys.join('|')
  const fetcher = useMemo<Fetcher<T>>(
    () => buildQueueFetcher<T>(data, searchKeyStr.split('|')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataKey, searchKeyStr],
  )

  const firstName = fields?.[0]?.name ?? ''
  const canAdd = !firstName || Boolean(form[firstName]?.trim())

  const add = async () => {
    if (!onCreate || !canAdd) return
    setBusy(true)
    try {
      const msg = await onCreate(form)
      toast(typeof msg === 'string' ? msg : S.createdOk, { tone: 'success' })
      setForm({})
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const rowActions = useMemo<RowAction<T>[]>(
    () =>
      (actions ?? []).map((a) => ({
        id: a.id,
        label: a.label,
        icon: a.icon,
        danger: a.danger,
        visible: a.visible,
        onSelect: async (r: T) => {
          try {
            await a.run(r)
            toast(S.updatedOk, { tone: 'success' })
            reload()
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      })),
    [actions, toast],
  )

  const bulk = useMemo<BulkAction<T>[]>(
    () =>
      (bulkActions ?? []).map((b) => ({
        id: b.id,
        label: b.label,
        icon: b.icon,
        danger: b.danger,
        onSelect: async (sel: Selection<T>) => {
          if (sel.mode !== 'rows') return
          try {
            await b.run(sel.rows)
            toast(S.updatedOk, { tone: 'success' })
            reload()
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      })),
    [bulkActions, toast],
  )

  return (
    <>
      <PageHeader title={title} screenId={screenId} description={description} />
      {fields && onCreate && (
        <div className="flex flex-wrap items-center gap-2 p-4 pb-0">
          {fields.map((f) =>
            f.options ? (
              <select
                key={f.name}
                value={form[f.name] ?? ''}
                onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
                className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary outline-none focus:border-border-strong"
              >
                <option value="">{f.placeholder}</option>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                key={f.name}
                type={f.type === 'number' ? 'number' : 'text'}
                value={form[f.name] ?? ''}
                onChange={(e) => setForm((s) => ({ ...s, [f.name]: e.target.value }))}
                placeholder={f.placeholder}
                spellCheck={false}
                className={`min-w-[140px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong ${
                  f.mono ? 'font-mono' : ''
                }`}
              />
            ),
          )}
          <button
            type="button"
            onClick={add}
            disabled={busy || !canAdd}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <Plus size={14} />
            {addLabel ?? S.add}
          </button>
        </div>
      )}
      <DataTable<T>
        key={reloadKey}
        screenId={screenId}
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        bulkActions={bulk}
        selectable={selectable}
        exportable
        emptyState={{ icon }}
      />
    </>
  )
}
